"use client";
import { Image as ImageIcon, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import type { DropEvent } from "react-aria";
import { DropZone, FileTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";

export interface FileUploadProps {
  /**
   * Current file URL or preview
   */
  value?: string;
  /**
   * Callback when file is selected
   */
  onChange?: (file: File | null, url?: string) => void;
  /**
   * Callback when file URL changes (for external upload handlers)
   */
  onUrlChange?: (url: string) => void;
  /**
   * Accepted file types
   */
  accept?: string;
  /**
   * Maximum file size in bytes
   */
  maxSize?: number;
  /**
   * Whether the component is disabled
   */
  isDisabled?: boolean;
  /**
   * Custom upload handler (e.g., for cloud storage)
   */
  onUpload?: (file: File) => Promise<string>;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Show image preview for image files
   */
  showPreview?: boolean;
  /**
   * Placeholder text
   */
  placeholder?: string;
}

/**
 * FileUpload
 *
 * A file upload component with drag-and-drop support.
 * Supports image preview, custom upload handlers, and file validation.
 */
export function FileUpload({
  value,
  onChange,
  onUrlChange,
  accept = "image/*",
  maxSize,
  isDisabled = false,
  onUpload,
  className,
  showPreview = true,
  placeholder = "Drop files here or click to browse",
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | undefined>(value);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError(null);

    // Validate file size
    if (maxSize && file.size > maxSize) {
      setError(`File size must be less than ${(maxSize / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    // Handle file preview for images
    if (showPreview && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }

    // If custom upload handler is provided, use it
    if (onUpload) {
      setIsUploading(true);
      try {
        const url = await onUpload(file);
        setPreview(url);
        onUrlChange?.(url);
      } catch (err) {
        setError("Failed to upload file");
        console.error("Upload error:", err);
      } finally {
        setIsUploading(false);
      }
    }

    onChange?.(file, preview);
  };

  const handleDrop = async (e: DropEvent) => {
    try {
      const items = e.items;
      if (items.length > 0) {
        const item = items[0];
        if (item.kind === "file") {
          const file = await item.getFile();
          if (file) {
            handleFile(file);
          }
        }
      }
    } catch (err) {
      setError("Failed to upload file");
      console.error("Upload error:", err);
    } finally {
      setIsDragging(false);
    }
  };

  const handleRemove = () => {
    setPreview(undefined);
    setError(null);
    onChange?.(null);
    onUrlChange?.("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={twMerge("w-full", className)}>
      <DropZone
        className={twMerge(
          "relative flex min-h-[200px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors",
          isDragging ? "border-action-brand bg-action-brand/5" : "border-border-muted hover:border-border-default",
          isDisabled && "pointer-events-none opacity-50",
          error && "border-feedback-error-border bg-feedback-error-background/50"
        )}
        onDrop={handleDrop}
        onDropEnter={() => setIsDragging(true)}
        onDropExit={() => setIsDragging(false)}
      >
        {preview ? (
          <div className="relative flex h-full w-full items-center justify-center p-4">
            {showPreview && (preview.startsWith("data:image") || preview.startsWith("http")) ? (
              <img src={preview} alt="Preview" className="max-h-48 max-w-full rounded-md object-contain" />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <ImageIcon className="h-12 w-12 text-text-secondary" />
                <span className="text-sm text-text-secondary">File uploaded</span>
              </div>
            )}
            {!isDisabled && (
              <Button
                variant="icon"
                size="sm"
                onPress={handleRemove}
                className="absolute right-2 top-2 bg-background-surface shadow-md"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 p-6">
            <Upload className="h-12 w-12 text-text-secondary" />
            <div className="text-center">
              <p className="text-sm text-text-primary">{placeholder}</p>
              <p className="mt-1 text-xs text-text-secondary">
                {accept === "image/*" ? "PNG, JPG, GIF up to 10MB" : `Accepts ${accept}`}
              </p>
            </div>
            <FileTrigger
              acceptedFileTypes={accept.split(",")}
              onSelect={(e) => {
                const files = Array.from(e || []);
                if (files.length > 0) {
                  handleFile(files[0]);
                }
              }}
            >
              <Button variant="secondary" size="sm" isDisabled={isDisabled}>
                Choose File
              </Button>
            </FileTrigger>
          </div>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background-surface/80">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-action-brand border-t-transparent" />
              <span className="text-sm text-text-secondary">Uploading...</span>
            </div>
          </div>
        )}
      </DropZone>
      {error && <p className="mt-2 text-sm text-feedback-error-text">{error}</p>}
    </div>
  );
}
